import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "recharts"

import type { ChartConfig } from "@/components/ui/chart"
import {
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart"
import type { DashboardSmaugTypeEnum } from "@/enums"
import { useDashboardSmaug } from "@/hooks"
interface DashboardVolumeDayWeekRecurringChartProps {
  dashboardType: DashboardSmaugTypeEnum
  searchParams: SearchParamsType
}
export const DashboardVolumeDayWeekRecurringChart = ({
  dashboardType,
  searchParams,
}: DashboardVolumeDayWeekRecurringChartProps) => {
  const { t } = useTranslation()
  const { data } = useDashboardSmaug({
    ...searchParams,
    dashboardType,
  })
  const getWeekDay = (weekDay: string) => {
    switch (weekDay) {
      case "MONDAY":
      case "Segunda-feira":
        return {
          label: "Segunda-feira",
          abbr: "Seg",
        }
      case "TUESDAY":
      case "Terça-feira":
        return {
          label: "Terça-feira",
          abbr: "Ter",
        }
      case "WEDNESDAY":
      case "Quarta-feira":
        return {
          label: "Quarta-feira",
          abbr: "Qua",
        }
      case "THURSDAY":
      case "Quinta-feira":
        return {
          label: "Quinta-feira",
          abbr: "Qui",
        }
      case "FRIDAY":
      case "Sexta-feira":
        return {
          label: "Sexta-feira",
          abbr: "Sex",
        }
      case "SATURDAY":
      case "Sábado":
        return {
          label: "Sábado",
          abbr: "Sab",
        }
      case "SUNDAY":
      case "Domingo":
        return {
          label: "Domingo",
          abbr: "Dom",
        }
      default:
        return {
          label: "",
          abbr: "",
        }
    }
  }
  const chartData = data?.indicator?.map((indicator) => ({
    weekDay: getWeekDay(indicator.key).label,
    value: +indicator.value.replace("%", ""),
  }))
  const chartConfig = {
    value: {
      label: "Dia da semana",
      color: "hsl(var(--chart-1))",
    },
    weekDay: {
      label: "Dia da semana",
      color: "hsl(var(--chart-1))",
    },
  } satisfies ChartConfig

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-sm text-muted-foreground">
        {t("pages.dashboard.noDataAvailable")}
      </div>
    )
  }

  return (
    <ChartContainer
      config={chartConfig}
      className="-ml-6 max-h-[200px] w-full"
    >
      <LineChart accessibilityLayer data={chartData}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="weekDay"
          tickLine={false}
          axisLine={false}
          tickCount={10}
          tickFormatter={(value) => {
            return getWeekDay(value).abbr
          }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={15}
          tickCount={5}
        />
        <ChartTooltip
          cursor={false}
          content={({ label, payload }) => {
            return (
              <div className="bg-white p-1 shadow-lg">
                {label}{" "}
                {payload && payload[0]?.payload.value}%
              </div>
            )
          }}
        />
        <Line
          dataKey="value"
          type="linear"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ChartContainer>
  )
}
